import React, { FC, useEffect, useRef, useState } from "react";
import { ViewProgramTemplateWrapper } from "./ViewProgramTemplate.style";
import { ViewProgramTemplateHeader } from "./components/viewProgramTemplateHeader/ViewProgramTemplateHeader";
import {
  AccordionHeaderText,
  AccordionHeader,
  MuiBox,
  MuiButtonTertiary,
  MuiGrid,
  MultiOptionsDataType,
  MuiDoubleLabel,
  MuiTypography,
} from "../../components";
import { Add, Remove } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetCompatibleTemplateList,
  useGetListOfActivitiesById,
  useGetListOfDeliverables,
  useGetProgramTemplateById,
} from "../../api/services/programTemplate/programTemplateApis";
import ViewProgramTemplateProfile from "./components/viewProgramTemplateProfile/ViewProgramTemplateProfile";
import { useDispatch } from "react-redux";
import {
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import { ViewProgramTemplateFooter } from "./components/viewProgramTemplateFooter/ViewProgramTemplateFooter";
import { AccordionStatusViewProgramTemplate } from "./modelsAndSchemas/Models";
import { useSiteCore } from "../../hooks/useSiteCore";
import { createProgramSiteCoreKeys } from "../createProgramTemplate/siteCore/SiteCoreKeys";
import {
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
} from "../../helpers/constants";
import { ViewLocale } from "./components/viewLocale/ViewLocale";
import {
  ProgramTemplateActivitiesRowValue,
  ProgramTemplateDeliverablesRowValue,
} from "../createProgramTemplate/modelsAndSchemas/Models";
import { isNotEmpty } from "../../helpers/utils";
import { CreateProgramTemplateActivitiesAndDeliverables } from "../createProgramTemplate/components/createProgramTemplateActivitiesAndDeliverables/CreateProgramTemplateActivitiesAndDeliverables";
import { CompatibleTemplateList } from "../createProgramTemplate/components/compatibleTemplate/components/compatibleTemplateList/CompatibleTemplateList";
import PermissionWrapper from "../../components/permissionWrapper/PermissionWrapper";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useGridContainer } from "../../hooks/useGridContainer";
import { MuiDoubleLabelWithChip } from "../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";

export const pageId = PAGE_IDS.VIEW_PROGRAM_TEMPLATE;

const ViewProgramTemplate: FC = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { commonARIALabel } = useNavigationSiteCore();
  const gridRef = useRef<HTMLDivElement>(null); // Create a reference for the MuiGrid
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const { commonLabels, fetchComponentTitle, fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const [showLocaleModal, setShowLocaleModal] = useState(false);
  const [accordionStatus, setAccordionStatus] =
    useState<AccordionStatusViewProgramTemplate>({
      profile: true,
      activity: false,
      compatibleTemplate: false,
      all: false,
    });

  const {
    refetchWithParams: getCompatibleTemplateList,
    data: compatibleTemplateList,
    isFetching: isTemplateLoading,
  } = useGetCompatibleTemplateList();

  const {
    data: listOfActivitiesData,
    isLoading: isSearchActivityPending,
    refetchWithParams: getListOfActivitiesApi,
    isError: isListOfActivityError,
  } = useGetListOfActivitiesById();

  const {
    data: listOfDeliverablesData,
    isLoading: isSearchDeliverablePending,
    isError: isListOfDeliverablesError,
    refetchWithParams: getListOfDeliverablesApi,
  } = useGetListOfDeliverables();

  const {
    mutateAsync: getProgramTemplateById,
    data: programTemplateData,
    isPending: isLoading,
    isError,
  } = useGetProgramTemplateById();

  const [selectedCountries, setSelectedCountries] = useState<
    MultiOptionsDataType[]
  >([]);

  useEffect(() => {
    if (id) {
      getProgramTemplateById(id);
      getCompatibleTemplateList({ templateId: id });
    }
  }, [id]);

  useEffect(() => {
    if (isError || isListOfActivityError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isError, programTemplateData, isListOfActivityError]);

  useEffect(() => {
    if (isError || isListOfDeliverablesError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isError, programTemplateData, isListOfDeliverablesError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading || isSearchActivityPending || isTemplateLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading, isSearchActivityPending, isTemplateLoading]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading || isSearchDeliverablePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading, isSearchDeliverablePending]);

  useEffect(() => {
    if (isNotEmpty(programTemplateData) && id) {
      getListOfActivitiesApi(id, programTemplateData?.programFrameWorkId);
      getListOfDeliverablesApi(id, programTemplateData?.programFrameWorkId);
    }

    if (programTemplateData?.countries?.length) {
      const countriesData = programTemplateData.countries.map((country: { countryName: string; }) => ({
        value: country.countryName,
        label: country.countryName,
      }));
      setSelectedCountries(countriesData);
    } else { 
      setSelectedCountries([]);
    }
  }, [programTemplateData]);

  const handleExpandCollapseAllChange = () => {
    const newStatus = !accordionStatus.all;
    setAccordionStatus({
      profile: newStatus,
      activity: newStatus,
      compatibleTemplate: newStatus,
      all: newStatus,
    });
  };

  const handleIndividualAccordionChange = (accordionName: string) => {
    setAccordionStatus((prevStatus) => ({
      ...prevStatus,
      [accordionName]: !prevStatus[accordionName],
    }));
  };

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const handleNavigateBack = () => {
    navigate(PAGE_URLS.PROGRAM_TEMPLATE);
  };

  return (
    <ViewProgramTemplateWrapper data-testid="program-template">
      <ViewProgramTemplateHeader title={programTemplateData?.name ?? ""} />

      <MuiGrid className="header-collapse">
        <MuiButtonTertiary
          onClick={handleExpandCollapseAllChange}
          id={`${pageId}-collapse-button`}
        >
          {accordionStatus.all
            ? commonLabels.collapseAll
            : commonLabels.expandAll}
          {accordionStatus.all ? (
            <Remove color={"primary"} />
          ) : (
            <Add color={"primary"} />
          )}
        </MuiButtonTertiary>
      </MuiGrid>

      <PermissionWrapper permission={MODULE_PERMISSIONS.VIEW_PROFILE}>
        <AccordionHeader
          title={renderHeader(
            fetchComponentTitle(createProgramSiteCoreKeys.profileSection),
          )}
          onChange={() => handleIndividualAccordionChange("profile")}
          className="accordion-resource-header"
          isExpanded={accordionStatus.profile}
        >
          <ViewProgramTemplateProfile
            pageId={pageId}
            programTemplateData={programTemplateData}
            setShowLocaleModal={() => setShowLocaleModal(true)}
          />
        </AccordionHeader>
      </PermissionWrapper>

      <MuiBox className="separator-space" />

      <PermissionWrapper
        permission={MODULE_PERMISSIONS.VIEW_COMPATIBLE_TEMPLATES}
      >
        <AccordionHeader
          title={renderHeader(
            fetchComponentTitle(
              createProgramSiteCoreKeys.compatibleTemplateKey,
            ),
          )}
          onChange={() => handleIndividualAccordionChange("compatibleTemplate")}
          className="accordion-resource-header"
          isExpanded={accordionStatus.compatibleTemplate}
        >

          <MuiGrid container className="main-container">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiBox>
                <MuiDoubleLabel
                  label={"Region Type"}
                  value={ selectedCountries.length ?   
                      fetchValueByKeyOfDropdown(
                        createProgramSiteCoreKeys.compatibleTemplateKey,
                        createProgramSiteCoreKeys.regionTypeList,
                        createProgramSiteCoreKeys.multipleCountries,
                      )
                    :
                      fetchValueByKeyOfDropdown(
                        createProgramSiteCoreKeys.compatibleTemplateKey,
                        createProgramSiteCoreKeys.regionTypeList,
                        createProgramSiteCoreKeys.regionTypeGlobal,
                      ) 
                  }
                  isValueWithEllipsis
                />

              </MuiBox>
            </MuiGrid>
            {selectedCountries.length > 0 && 
            <MuiGrid size={gridSize} className={gridSizeMediumClass} ref={gridRef}>
              <MuiDoubleLabelWithChip
                label={fetchLabel(
                  createProgramSiteCoreKeys.compatibleTemplateKey,
                  createProgramSiteCoreKeys.countrys,
                )}
                value={selectedCountries}
                chipPopoverHeaderLabel={commonLabels.selectedCountries}
                id={"selected-countries"}
                chipPopoverGridRef={gridRef}
                showMoreARIALabel={commonARIALabel.showMoreDetails}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            </MuiGrid>
            }
          </MuiGrid>
          {selectedCountries.length > 0 && 
          <MuiBox className="fields-wrapper">
            <MuiTypography variant="h2SemiBold" component={"h2"}>
               {fetchLabel(
                  createProgramSiteCoreKeys.compatibleTemplateKey,
                  createProgramSiteCoreKeys.compatibleTemplateSubTitle,
                )}
            </MuiTypography>

            <CompatibleTemplateList
              pageId={pageId}
              rows={compatibleTemplateList ?? []}
              templateId={id ?? ""}
              isViewTemplate={true}
              />
            </MuiBox>
          } 
        </AccordionHeader>
      </PermissionWrapper>

      <MuiBox className="separator-space" />

      <PermissionWrapper
        permission={MODULE_PERMISSIONS.VIEW_ACTIVITIES_AND_DELIVERABLES}
      >
        <AccordionHeader
          title={renderHeader(
            fetchComponentTitle(createProgramSiteCoreKeys.activitiesList),
          )}
          onChange={() => handleIndividualAccordionChange("activity")}
          isExpanded={accordionStatus.activity}
        >
          <CreateProgramTemplateActivitiesAndDeliverables
            rows={
              (listOfActivitiesData as ProgramTemplateActivitiesRowValue[]) ||
              []
            }
            deliverableRows={
              (listOfDeliverablesData?.deliverables as ProgramTemplateDeliverablesRowValue[]) ||
              []
            }
            isViewTemplate={true}
            onCreateActivityClick={() => {}}
            successHandler={() => {}}
            successHandlerDeliverables={() => {}}
            programTemplateId={id}
            onCreateDeliverableClick={() => {}}
          />
        </AccordionHeader>
      </PermissionWrapper>

      <ViewProgramTemplateFooter
        pageId={pageId}
        handleNavigateBack={handleNavigateBack}
        isVersionLatest={true}
      />

      {showLocaleModal && (
        <ViewLocale
          pageId={pageId}
          show={showLocaleModal}
          handleCloseModal={() => setShowLocaleModal(false)}
          englishName={programTemplateData?.name ?? ""}
          localizedProgramTemplateNames={
            programTemplateData?.localizedProgramTemplateNames ?? []
          }
        />
      )}
    </ViewProgramTemplateWrapper>
  );
};

export default ViewProgramTemplate;
